import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { LANGUAGE_OPTIONS, useI18n, type Lang } from '@/i18n/I18nProvider';

interface LanguageSwitcherProps {
  /** Hide flag emojis (e.g. when running on platforms with poor emoji rendering). */
  hideFlags?: boolean;
  /** Use the compact (icon-only on small screens) variant. */
  compact?: boolean;
  className?: string;
}

export default function LanguageSwitcher({
  hideFlags = false,
  compact = true,
  className,
}: LanguageSwitcherProps) {
  const { lang, setLang, t } = useI18n();
  const current = LANGUAGE_OPTIONS.find((o) => o.code === lang) ?? LANGUAGE_OPTIONS[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t('header.switch_language', 'Language')}
          className={
            'h-9 gap-1.5 rounded-xl border-slate-200/80 bg-white/85 px-2.5 text-slate-700 shadow-soft backdrop-blur-md hover:bg-white sm:px-3 ' +
            (className ?? '')
          }
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          {!hideFlags && <span aria-hidden="true">{current.flag}</span>}
          <span className={compact ? 'hidden sm:inline' : ''}>{current.shortLabel}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>{t('header.switch_language', 'Language')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {LANGUAGE_OPTIONS.map((option) => {
          const isActive = option.code === lang;
          return (
            <DropdownMenuItem
              key={option.code}
              onSelect={() => setLang(option.code as Lang)}
              className="cursor-pointer"
            >
              {!hideFlags && (
                <span className="mr-2" aria-hidden="true">
                  {option.flag}
                </span>
              )}
              <span className="flex-1">{option.label}</span>
              {isActive && <Check className="ml-2 h-4 w-4 text-primary" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
